import { CommonModule } from '@angular/common';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, ViewEncapsulation, input, computed } from '@angular/core';
import { IsActiveMatchOptions, Route, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SecurityModuleTag, SecurityModuleUtility } from '@jms/model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { SideNavItemBaseComponent } from './sidenav.item.base.component';
import { SideNavMenuComponent } from './sidenav.menu.component';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';

interface SidenavTagBadge { label: string; backgroundColor: string; color: string; }

// Display config per security-module tag rendered as a sidenav badge. Beta keeps the existing
// teal styling; Test is a louder orange to signal a rawer, more experimental surface.
const SIDENAV_TAG_BADGES: Partial<Record<SecurityModuleTag, SidenavTagBadge>> = {
    [SecurityModuleTag.Beta]: { label: 'BETA', backgroundColor: 'var(--component-badge-color-priority-p6-background, rgba(13, 148, 136, 0.25))', color: 'var(--component-badge-color-priority-p6-text-subtle, #2DD4BF)' },
    [SecurityModuleTag.Test]: { label: 'TEST', backgroundColor: 'rgba(249, 115, 22, 0.25)', color: '#FB923C' },
};

@UntilDestroy()
@Component({
    selector: 'sidenav-item',
    template: `

    <ng-container *ngIf="route?.children?.length; then hasMenu; else noMenu"></ng-container>

    <ng-template #hasMenu>

        <div class="side-nav-item has-menu" [attr.data-tour-target]="route.path"
             *ngIf="route.data.icon"
             cdkOverlayOrigin
             #trigger="cdkOverlayOrigin"
             (mouseenter)="open = true"
             [ngClass]="{ active: active} "
             >

            <div class="icon-container">
                <i class="fa" [ngClass]="route.data.icon"></i>
                @if ($hasBadge()) {
                    <span class="badge" [class.plus]="$badgeLabel().includes('+')">{{ $badgeLabel() }}</span>
                }
            </div>
            <span class='title'>{{route?.data?.title}}</span>
            @if(tagBadge(); as tag) {
                <w-tag [label]="tag.label" [mode]="BadgeMode.Squared" size="xs" [backgroundColor]="tag.backgroundColor" [color]="tag.color"/>
            }

            <ng-template cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="trigger"]
                [cdkConnectedOverlayOpen]="open"]
                [cdkConnectedOverlayPositions]="positions"]
                [cdkConnectedOverlayScrollStrategy]="overlay.scrollStrategies.close()"]
                (attach)="onAttached()"
                (detach)="open = false"
                (overlayOutsideClick)="open = false"
                >
                <!-- <sidenav-menu [route]="route" (clicked)="open = false" (mouseleave)="open = false"></sidenav-menu> -->
                <sidenav-menu [route]="route" (clicked)="open = false" (mouseleave)="open = false"></sidenav-menu>
            </ng-template>

            <div cdkOverlayContainer ></div>

        </div>



    </ng-template>
    <ng-template #noMenu>
        <a class="side-nav-item" routerLink="/{{route.path}}" routerLinkActive="active" *ngIf="route.data.icon" (mouseenter)="onForceClose()" >
            <div class="icon-container">
                <i class="fa" [ngClass]="route.data.icon"></i>
                @if ($hasBadge()) {
                    <span class="badge" [class.plus]="$badgeLabel().includes('+')">{{ $badgeLabel() }}</span>
                }
            </div>
            <span class='title'>{{route?.data?.title}}</span>
            @if(tagBadge(); as tag) {
                <w-tag [label]="tag.label" [mode]="BadgeMode.Squared" size="xs" [backgroundColor]="tag.backgroundColor" [color]="tag.color"/>
            }
        </a>
    </ng-template>


    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, RouterLink, RouterLinkActive, OverlayModule, CdkOverlayOrigin, CdkConnectedOverlay, SideNavMenuComponent, BadgeComponent]
})
export class SideNavItemComponent extends SideNavItemBaseComponent {

    @Input() public route: Route;

    public $badgeCount = input<number>(0, { alias: 'badgeCount' });
    public $badgeLabel = input<string>('', { alias: 'badgeLabel' });

    protected $hasBadge = computed(() => this.$badgeCount() > 0);

    protected BadgeMode = BadgeMode;

    public positions = [{
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
    } as ConnectedPosition];

    public isActiveOptions: IsActiveMatchOptions = {
        matrixParams: 'ignored',
        queryParams: 'ignored',
        paths: 'subset',
        fragment: 'ignored'
    };

    constructor(
        public overlay: Overlay,
        protected cdr: ChangeDetectorRef,
        public router: Router
    ) {
        super(cdr);
    }

    ngOnInit(): void {
        this.active = this.router.isActive(this.route.path, this.isActiveOptions);

        if (this.route?.children?.length) {
            this.router
                .events
                .pipe(
                    untilDestroyed(this),
                    tap(x => {
                        this.active = this.router.isActive(this.route.path, this.isActiveOptions);
                        this.cdr.markForCheck();
                    })
                )
                .subscribe();
        }
    }

    ngOnDestroy(): void { }

    public tagBadge(): SidenavTagBadge | null {
        if (!this.route?.data?.securityModule) return null;

        const securityModule = SecurityModuleUtility.systemModules.find(m => m.name === this.route.data.securityModule);
        const tags = securityModule?.tags ?? [];

        // Test wins over Beta when a module carries both, since it signals the rawer surface.
        if (tags.includes(SecurityModuleTag.Test)) return SIDENAV_TAG_BADGES[SecurityModuleTag.Test] ?? null;
        if (tags.includes(SecurityModuleTag.Beta)) return SIDENAV_TAG_BADGES[SecurityModuleTag.Beta] ?? null;
        return null;
    }
}
